import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Plus, Trash2, Percent, DollarSign, ChevronLeft, ChevronRight, Filter, X, Users, ChevronDown, ChevronUp
} from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { Checkbox } from '../components/ui/checkbox';
import { AmountInput } from '../components/ui/amount-input';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SHARE_COLORS = [
  '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#f97316', '#14b8a6', '#6366f1',
];

function ShareCard({ project, shareData, defaultExpanded = false }) {
  const { t } = useTranslation('assignments');
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { shares = [], total_allocated = 0 } = shareData || {};
  const remaining = Math.max(0, 100 - total_allocated);

  const pieData = [
    ...shares.map((s, i) => ({ name: s.employee_name, value: s.percentage, fill: SHARE_COLORS[i % SHARE_COLORS.length] })),
    ...(remaining > 0 ? [{ name: t('shares.available'), value: remaining, fill: '#e2e8f0' }] : []),
  ];

  const isFull = total_allocated >= 100;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{project.name}</h3>
            {project.project_type && (
              <span className="text-xs text-slate-500 dark:text-slate-400 capitalize">{t(`projectTypes.${project.project_type}`, { defaultValue: project.project_type })}</span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 ml-2">
            <Users className="h-3.5 w-3.5" />
            <span>{shares.length}</span>
          </div>
        </div>

        {/* Mini chart + progress */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 flex-shrink-0">
            {shares.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius={14} outerRadius={22} strokeWidth={0}>
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="w-12 h-12 rounded-full border-2 border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center">
                <span className="text-[10px] text-slate-400">0%</span>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-500 dark:text-slate-400">{t('shares.allocated')}</span>
              <span className={`font-semibold ${isFull ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-700 dark:text-slate-300'}`}>
                {total_allocated}%
              </span>
            </div>
            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${isFull ? 'bg-emerald-500' : total_allocated > 80 ? 'bg-amber-500' : 'bg-blue-500'}`}
                style={{ width: `${Math.min(total_allocated, 100)}%` }}
              />
            </div>
            <div className="flex justify-between text-xs mt-1">
              <span className="text-slate-500 dark:text-slate-400">{t('shares.remaining')}</span>
              <span className={`font-semibold ${remaining > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                {remaining}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Expand toggle */}
      {shares.length > 0 && (
        <>
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-center gap-1 py-2 text-xs text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 border-t border-slate-100 dark:border-slate-700 transition-colors"
          >
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {expanded ? t('shares.hideDetails') : t('shares.viewDetails')}
          </button>

          {expanded && (
            <div className="px-4 pb-4 border-t border-slate-100 dark:border-slate-700">
              <div className="flex items-center gap-3 mt-3 mb-3">
                <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 flex-shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius="35%" outerRadius="50%" strokeWidth={1} stroke="#fff">
                        {pieData.map((entry, i) => (
                          <Cell key={i} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(val) => `${val}%`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-1.5">
                  {shares.map((share, i) => (
                    <div key={share.employee_id} className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: SHARE_COLORS[i % SHARE_COLORS.length] }} />
                      <span className="text-sm text-slate-700 dark:text-slate-300 flex-1 truncate">{share.employee_name}</span>
                      <span className="text-xs text-slate-500 dark:text-slate-400">{share.position}</span>
                      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100 ml-auto">{share.percentage}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Assignments() {
  const { t } = useTranslation('assignments');
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  // Share allocation data per project
  const [projectShares, setProjectShares] = useState({});
  const [sharesLoading, setSharesLoading] = useState(false);
  const [showShares, setShowShares] = useState(true);
  const [showUnassigned, setShowUnassigned] = useState(false);

  // Remaining % for form preview
  const [formRemaining, setFormRemaining] = useState(null);

  // Employee's assigned projects for the form
  const [employeeProjects, setEmployeeProjects] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filters
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [formData, setFormData] = useState({
    employee_id: '',
    project_id: '',
    payment_type: 'percentage',
    percentage: '',
    fixed_amount: '',
    fixed_currency: 'USD',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });

  // Period mode
  const [periodMode, setPeriodMode] = useState(false);
  const [periodFrom, setPeriodFrom] = useState({ month: 1, year: new Date().getFullYear() });
  const [periodTo, setPeriodTo] = useState({ month: 12, year: new Date().getFullYear() });
  const [bulkLoading, setBulkLoading] = useState(false);

  const months = t('common:months', { returnObjects: true });
  const monthsShort = t('common:monthsShort', { returnObjects: true });

  // Determine effective month/year for shares display
  const effectiveMonth = filterMonth ? parseInt(filterMonth) : new Date().getMonth() + 1;
  const effectiveYear = filterYear ? parseInt(filterYear) : new Date().getFullYear();

  const fetchAssignments = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: page.toString(), per_page: perPage.toString() });
      if (filterEmployee) params.append('employee_id', filterEmployee);
      if (filterProject) params.append('project_id', filterProject);
      if (filterMonth) params.append('month', filterMonth);
      if (filterYear) params.append('year', filterYear);

      const res = await axios.get(`${API_URL}/api/assignments?${params}`, { withCredentials: true });
      setAssignments(res.data.items);
      setTotalItems(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      toast.error(t('toast.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [page, perPage, filterEmployee, filterProject, filterMonth, filterYear, t]);

  const fetchReferenceData = useCallback(async () => {
    try {
      const [employeesRes, projectsRes] = await Promise.all([
        axios.get(`${API_URL}/api/employees?per_page=100`, { withCredentials: true }),
        axios.get(`${API_URL}/api/projects`, { withCredentials: true })
      ]);
      setEmployees(employeesRes.data.items || employeesRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  }, []);

  const fetchProjectShares = useCallback(async (projectList, m, y) => {
    if (!projectList || projectList.length === 0) {
      setProjectShares({});
      return;
    }
    const uniqueProjectIds = [...new Set(projectList.map((p) => p.project_id))];
    setSharesLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/projects/shares/batch`,
        { project_ids: uniqueProjectIds, month: m, year: y },
        { withCredentials: true }
      );
      const dataByProjectId = response?.data || {};
      const sharesMap = {};
      uniqueProjectIds.forEach((id) => {
        sharesMap[id] = dataByProjectId[id] || { shares: [], total_allocated: 0, remaining: 100 };
      });
      setProjectShares(sharesMap);
    } catch (err) {
      console.error('Error fetching shares:', err);
      const fallback = {};
      uniqueProjectIds.forEach((id) => {
        fallback[id] = { shares: [], total_allocated: 0, remaining: 100 };
      });
      setProjectShares(fallback);
    } finally {
      setSharesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  // Fetch shares when projects load or month/year filter changes
  useEffect(() => {
    if (projects.length > 0) {
      const activeProjs = projects.filter(p => p.status === 'active');
      fetchProjectShares(activeProjs, effectiveMonth, effectiveYear);
    }
  }, [projects, effectiveMonth, effectiveYear, fetchProjectShares]);

  // Refresh shares after assignments change
  useEffect(() => {
    if (projects.length > 0 && !loading) {
      const activeProjs = projects.filter(p => p.status === 'active');
      fetchProjectShares(activeProjs, effectiveMonth, effectiveYear);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [filterEmployee, filterProject, filterMonth, filterYear, perPage]);

  // Fetch employee's assigned projects when employee changes in form
  useEffect(() => {
    if (formData.employee_id) {
      const emp = employees.find(e => e.employee_id === formData.employee_id);
      if (emp && emp.user_id) {
        axios.get(`${API_URL}/api/users/${emp.user_id}/projects`, { withCredentials: true })
          .then((res) => {
            const ids = res.data.map(p => p.project_id);
            setEmployeeProjects(ids);
            // Reset project if not in employee's assigned list
            if (formData.project_id && !ids.includes(formData.project_id)) {
              setFormData(prev => ({ ...prev, project_id: '' }));
            }
          })
          .catch(() => setEmployeeProjects(null));
      } else {
        setEmployeeProjects(null);
      }
    } else {
      setEmployeeProjects(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.employee_id, employees]);

  // Fetch remaining % for form when project/month/year changes in form
  useEffect(() => {
    if (formData.project_id && formData.payment_type === 'percentage') {
      axios.get(`${API_URL}/api/projects/${formData.project_id}/shares?month=${formData.month}&year=${formData.year}`, { withCredentials: true })
        .then((res) => {
          // Exclude current employee's existing allocation
          const otherTotal = (res.data.shares || [])
            .filter(s => s.employee_id !== formData.employee_id)
            .reduce((sum, s) => sum + (s.percentage || 0), 0);
          setFormRemaining(Math.round((100 - otherTotal) * 100) / 100);
        })
        .catch(() => setFormRemaining(null));
    } else {
      setFormRemaining(null);
    }
  }, [formData.project_id, formData.month, formData.year, formData.payment_type, formData.employee_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (periodMode) {
        // Bulk create for period
        setBulkLoading(true);
        const payload = {
          employee_id: formData.employee_id,
          project_id: formData.project_id,
          payment_type: formData.payment_type,
          from_month: periodFrom.month,
          from_year: periodFrom.year,
          to_month: periodTo.month,
          to_year: periodTo.year,
        };
        if (formData.payment_type === 'percentage') {
          payload.percentage = parseFloat(formData.percentage);
        } else {
          payload.fixed_amount = parseFloat(formData.fixed_amount);
          payload.fixed_currency = formData.fixed_currency;
        }
        const res = await axios.post(`${API_URL}/api/assignments/bulk`, payload, { withCredentials: true });
        const msg = t('toast.bulkCreated', { count: res.data.total, created: res.data.created, updated: res.data.updated });
        if (res.data.errors?.length > 0) {
          toast.warning(t('toast.bulkWithErrors', { message: msg, errors: res.data.errors.join('; ') }));
        } else {
          toast.success(msg);
        }
        setBulkLoading(false);
        setOpen(false);
        resetForm();
        fetchAssignments();
        return;
      }

      const payload = {
        employee_id: formData.employee_id,
        project_id: formData.project_id,
        payment_type: formData.payment_type,
        month: formData.month,
        year: formData.year
      };

      if (formData.payment_type === 'percentage') {
        payload.percentage = parseFloat(formData.percentage);
      } else {
        payload.fixed_amount = parseFloat(formData.fixed_amount);
        payload.fixed_currency = formData.fixed_currency;
      }

      await axios.post(`${API_URL}/api/assignments`, payload, { withCredentials: true });
      toast.success(t('toast.created'));
      setOpen(false);
      resetForm();
      fetchAssignments();
    } catch (error) {
      console.error('Error creating assignment:', error);
      toast.error(error.response?.data?.detail || t('toast.createFailed'));
      setBulkLoading(false);
    }
  };

  const handleDelete = async (assignmentId) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await axios.delete(`${API_URL}/api/assignments/${assignmentId}`, { withCredentials: true });
      toast.success(t('toast.deleted'));
      fetchAssignments();
    } catch (error) {
      console.error('Error deleting assignment:', error);
      toast.error(t('toast.deleteFailed'));
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      project_id: '',
      payment_type: 'percentage',
      percentage: '',
      fixed_amount: '',
      fixed_currency: 'USD',
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear()
    });
    setFormRemaining(null);
    setEmployeeProjects(null);
    setPeriodMode(false);
    setPeriodFrom({ month: 1, year: new Date().getFullYear() });
    setPeriodTo({ month: 12, year: new Date().getFullYear() });
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(e => e.employee_id === employeeId);
    return employee ? employee.full_name : t('unknown');
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.project_id === projectId);
    return project ? project.name : t('unknown');
  };

  const formatPayment = (assignment) => {
    if (assignment.payment_type === 'fixed' && assignment.fixed_amount) {
      const currency = assignment.fixed_currency || 'USD';
      if (currency === 'UZS') {
        return `${assignment.fixed_amount.toLocaleString()} UZS`;
      }
      return `$${assignment.fixed_amount.toLocaleString()}`;
    }
    return `${assignment.percentage || 0}%`;
  };

  const clearFilters = () => {
    setFilterEmployee('');
    setFilterProject('');
    setFilterMonth('');
    setFilterYear('');
  };

  const hasActiveFilters = filterEmployee || filterProject || filterMonth || filterYear;

  // Only active projects appear on the Assignments page (paused/completed excluded)
  const activeProjects = projects.filter(p => p.status === 'active');

  // When employee filter is active, show projects where employee has shares OR assignments
  const displayProjects = filterEmployee
    ? activeProjects.filter(p => {
        const sd = projectShares[p.project_id];
        const inShares = sd && sd.shares && sd.shares.some(s => s.employee_id === filterEmployee);
        const inAssignments = assignments.some(a => a.project_id === p.project_id);
        return inShares || inAssignments;
      })
    : activeProjects;

  // Projects that have any allocation for share display
  const projectsWithShares = displayProjects.filter(p => {
    const sd = projectShares[p.project_id];
    return sd && sd.shares && sd.shares.length > 0;
  });
  const projectsWithoutShares = displayProjects.filter(p => {
    const sd = projectShares[p.project_id];
    return !sd || !sd.shares || sd.shares.length === 0;
  });

  if (loading && assignments.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="assignments-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 dark:text-slate-100 tracking-tight" style={{ fontFamily: "'Outfit', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
            {t('title')}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
            {t('subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="relative">
            <Filter className="h-4 w-4 mr-2" />
            {t('filters.title')}
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 h-4 w-4 bg-blue-500 rounded-full text-[10px] text-white flex items-center justify-center">
                !
              </span>
            )}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => setOpen(true)} data-testid="add-assignment-btn">
                <Plus className="h-4 w-4 mr-2" />
                {t('addAssignment')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{t('dialog.title')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="employee_id">{t('form.employee')}</Label>
                  <Select value={formData.employee_id} onValueChange={(val) => setFormData({ ...formData, employee_id: val })}>
                    <SelectTrigger data-testid="employee-select">
                      <SelectValue placeholder={t('form.selectEmployee')} />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.filter(e => e.status === 'active').map((employee) => (
                        <SelectItem key={employee.employee_id} value={employee.employee_id}>
                          {employee.full_name} - {employee.position}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="project_id">{t('common:fields.project')}</Label>
                  <Select
                    value={formData.project_id}
                    onValueChange={(val) => setFormData({ ...formData, project_id: val })}
                    disabled={!formData.employee_id}
                  >
                    <SelectTrigger data-testid="project-select">
                      <SelectValue placeholder={!formData.employee_id ? t('form.selectEmployeeFirst') : t('form.selectProject')} />
                    </SelectTrigger>
                    <SelectContent>
                      {(() => {
                        const availableProjects = employeeProjects
                          ? activeProjects.filter(p => employeeProjects.includes(p.project_id))
                          : activeProjects;
                        return availableProjects.length === 0 ? (
                          <SelectItem value="" disabled>
                            {employeeProjects ? t('form.noAssignedProjects') : t('form.noActiveProjects')}
                          </SelectItem>
                        ) : (
                          availableProjects.map((project) => (
                            <SelectItem key={project.project_id} value={project.project_id}>
                              {project.name}
                            </SelectItem>
                          ))
                        );
                      })()}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    {employeeProjects ? t('form.assignedProjectsCount', { count: employeeProjects.length }) : t('form.onlyActiveShown')}
                  </p>
                </div>

                {/* Payment Type Selection */}
                <div>
                  <Label>{t('form.paymentType')}</Label>
                  <RadioGroup
                    value={formData.payment_type}
                    onValueChange={(val) => setFormData({ ...formData, payment_type: val })}
                    className="flex gap-4 mt-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="percentage" id="percentage" />
                      <Label htmlFor="percentage" className="flex items-center cursor-pointer">
                        <Percent className="h-4 w-4 mr-1" />
                        {t('form.percentageOfIncome')}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="fixed" id="fixed" />
                      <Label htmlFor="fixed" className="flex items-center cursor-pointer">
                        <DollarSign className="h-4 w-4 mr-1" />
                        {t('form.fixedSalary')}
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Conditional Fields based on payment type */}
                {formData.payment_type === 'percentage' ? (
                  <div>
                    <Label htmlFor="percentage">{t('form.revenuePercentage')}</Label>
                    <Input
                      id="percentage"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.percentage}
                      onChange={(e) => setFormData({ ...formData, percentage: e.target.value })}
                      placeholder={t('form.percentagePlaceholder')}
                      required
                      data-testid="percentage-input"
                    />
                    {formRemaining !== null && (
                      <div className={`mt-2 flex items-center gap-2 text-xs font-medium px-2.5 py-1.5 rounded-lg ${
                        formRemaining <= 0
                          ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                          : formRemaining < 20
                            ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
                            : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                      }`}>
                        <div className="w-full bg-slate-200 dark:bg-slate-600 rounded-full h-1.5 flex-1">
                          <div
                            className={`h-1.5 rounded-full ${formRemaining <= 0 ? 'bg-red-500' : formRemaining < 20 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                            style={{ width: `${Math.min(100 - formRemaining, 100)}%` }}
                          />
                        </div>
                        <span className="flex-shrink-0">
                          {formRemaining <= 0 ? t('form.fullyAllocated') : t('form.percentAvailable', { value: formRemaining })}
                        </span>
                      </div>
                    )}
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {t('form.percentageHint')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="fixed_amount">{t('form.fixedAmount')}</Label>
                      <AmountInput
                        id="fixed_amount"
                        value={formData.fixed_amount}
                        onChange={(val) => setFormData({ ...formData, fixed_amount: val })}
                        placeholder={t('form.fixedAmountPlaceholder')}
                        required
                        data-testid="fixed-amount-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="fixed_currency">{t('common:fields.currency')}</Label>
                      <Select value={formData.fixed_currency} onValueChange={(val) => setFormData({ ...formData, fixed_currency: val })}>
                        <SelectTrigger data-testid="currency-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">USD ($)</SelectItem>
                          <SelectItem value="UZS">{t('form.uzsOption')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {t('form.fixedHint')}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="month">{t('common:fields.month')}</Label>
                    <Select value={formData.month.toString()} onValueChange={(val) => setFormData({ ...formData, month: parseInt(val) })}>
                      <SelectTrigger data-testid="month-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {months.map((month, idx) => (
                          <SelectItem key={idx} value={(idx + 1).toString()}>
                            {month}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="year">{t('common:fields.year')}</Label>
                    <Select value={formData.year.toString()} onValueChange={(val) => setFormData({ ...formData, year: parseInt(val) })}>
                      <SelectTrigger data-testid="year-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026, 2027].map((year) => (
                          <SelectItem key={year} value={year.toString()}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Period Mode */}
                <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="period-mode"
                      checked={periodMode}
                      onCheckedChange={(checked) => setPeriodMode(checked)}
                    />
                    <Label htmlFor="period-mode" className="text-sm cursor-pointer">
                      {t('period.mode')}
                    </Label>
                  </div>
                  {periodMode && (
                    <div className="space-y-2 pt-1">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-slate-500">{t('period.from')}</Label>
                          <div className="flex gap-1.5">
                            <Select value={periodFrom.month.toString()} onValueChange={(v) => setPeriodFrom(prev => ({ ...prev, month: parseInt(v) }))}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {months.map((m, idx) => (
                                  <SelectItem key={idx} value={(idx + 1).toString()}>{monthsShort[idx]}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select value={periodFrom.year.toString()} onValueChange={(v) => setPeriodFrom(prev => ({ ...prev, year: parseInt(v) }))}>
                              <SelectTrigger className="h-8 text-xs w-[80px]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">{t('period.to')}</Label>
                          <div className="flex gap-1.5">
                            <Select value={periodTo.month.toString()} onValueChange={(v) => setPeriodTo(prev => ({ ...prev, month: parseInt(v) }))}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {months.map((m, idx) => (
                                  <SelectItem key={idx} value={(idx + 1).toString()}>{monthsShort[idx]}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select value={periodTo.year.toString()} onValueChange={(v) => setPeriodTo(prev => ({ ...prev, year: parseInt(v) }))}>
                              <SelectTrigger className="h-8 text-xs w-[80px]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-blue-600 dark:text-blue-400">
                        {(() => {
                          let count = 0;
                          let m = periodFrom.month, y = periodFrom.year;
                          while ((y < periodTo.year) || (y === periodTo.year && m <= periodTo.month)) {
                            count++; m++; if (m > 12) { m = 1; y++; }
                          }
                          return count > 0 ? t('period.willCreate', { count }) : t('period.invalid');
                        })()}
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={bulkLoading} data-testid="submit-assignment-btn">
                    {bulkLoading ? t('form.creating') : periodMode ? t('form.createForPeriod') : t('form.create')}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                    {t('common:actions.cancel')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters Bar */}
      {showFilters && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">{t('filters.title')}</h3>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs">
                <X className="h-3 w-3 mr-1" /> {t('filters.clearAll')}
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">{t('form.employee')}</Label>
              <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                <SelectTrigger className="h-9"><SelectValue placeholder={t('filters.allEmployees')} /></SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.employee_id} value={emp.employee_id}>{emp.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('common:fields.project')}</Label>
              <Select value={filterProject} onValueChange={setFilterProject}>
                <SelectTrigger className="h-9"><SelectValue placeholder={t('filters.allProjects')} /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('common:fields.month')}</Label>
              <Select value={filterMonth} onValueChange={setFilterMonth}>
                <SelectTrigger className="h-9"><SelectValue placeholder={t('filters.allMonths')} /></SelectTrigger>
                <SelectContent>
                  {months.map((m, idx) => (
                    <SelectItem key={idx} value={(idx + 1).toString()}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('common:fields.year')}</Label>
              <Select value={filterYear} onValueChange={setFilterYear}>
                <SelectTrigger className="h-9"><SelectValue placeholder={t('filters.allYears')} /></SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map((y) => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* Share Allocation Overview — projects assigned this month */}
      {(projectsWithShares.length > 0 || projectsWithoutShares.length > 0) && (
        <div>
          <button
            onClick={() => setShowShares(!showShares)}
            className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
          >
            {showShares ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            {t('shares.title', { month: months[effectiveMonth - 1], year: effectiveYear })}
            {sharesLoading && <span className="ml-2 animate-spin h-3 w-3 border-2 border-slate-400 border-t-transparent rounded-full" />}
          </button>
          {showShares && (
            <>
              {projectsWithShares.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {projectsWithShares.map((project) => (
                    <ShareCard
                      key={project.project_id}
                      project={project}
                      shareData={projectShares[project.project_id]}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 dark:text-slate-500">
                  {t('shares.none', { month: months[effectiveMonth - 1], year: effectiveYear })}
                </p>
              )}

              {/* Active projects with no assignment for this period — collapsed */}
              {projectsWithoutShares.length > 0 && (
                <div className="mt-4">
                  <button
                    onClick={() => setShowUnassigned(v => !v)}
                    className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                    data-testid="toggle-unassigned-projects"
                  >
                    {showUnassigned ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    {t('shares.unassigned', { n: projectsWithoutShares.length })}
                  </button>
                  {showUnassigned && (
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {projectsWithoutShares.map((project) => (
                        <ShareCard
                          key={project.project_id}
                          project={project}
                          shareData={projectShares[project.project_id]}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Assignments Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('form.employee')}</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('common:fields.project')}</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('form.paymentType')}</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('common:fields.amount')}</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('table.period')}</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('common:fields.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {assignments.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                    {hasActiveFilters ? t('table.emptyFiltered') : t('table.empty')}
                  </td>
                </tr>
              ) : (
                assignments.map((assignment) => (
                  <tr
                    key={assignment.assignment_id}
                    className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    data-testid={`assignment-row-${assignment.assignment_id}`}
                  >
                    <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300">
                      {getEmployeeName(assignment.employee_id)}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300">
                      {getProjectName(assignment.project_id)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        assignment.payment_type === 'fixed'
                          ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400'
                          : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                      }`}>
                        {assignment.payment_type === 'fixed' ? (
                          <><DollarSign className="h-3 w-3 mr-1" />{t('paymentTypes.fixed')}</>
                        ) : (
                          <><Percent className="h-3 w-3 mr-1" />{t('paymentTypes.percentage')}</>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400">
                        {formatPayment(assignment)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700 dark:text-slate-300">
                      {months[assignment.month - 1]} {assignment.year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(assignment.assignment_id)}
                        data-testid={`delete-assignment-${assignment.assignment_id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalItems > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-600 dark:text-slate-400">
                {t('pagination.showing', { from: ((page - 1) * perPage) + 1, to: Math.min(page * perPage, totalItems), total: totalItems })}
              </span>
              <Select value={perPage.toString()} onValueChange={(val) => setPerPage(parseInt(val))}>
                <SelectTrigger className="w-[100px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">{t('pagination.perPage', { n: 10 })}</SelectItem>
                  <SelectItem value="20">{t('pagination.perPage', { n: 20 })}</SelectItem>
                  <SelectItem value="50">{t('pagination.perPage', { n: 50 })}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={page === pageNum ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPage(pageNum)}
                    className="w-8 h-8 p-0"
                  >
                    {pageNum}
                  </Button>
                );
              })}
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      {totalItems > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                {t('stats.total')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
                {totalItems}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                {t('stats.onPage')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold text-blue-600">
                {assignments.length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                {t('stats.projectsOnPage')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold text-purple-600">
                {new Set(assignments.map(a => a.project_id)).size}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default Assignments;
